import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { Star, Calendar, MapPin, Camera, Quote } from 'lucide-react';
import { Card, CardContent } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { portfolioItems, testimonials } from '../data/mock';

export const Portfolio = () => {
  const [selectedCategory, setSelectedCategory] = useState('all');

  const categories = [
    { id: 'all', name: 'Toutes les réalisations' },
    { id: 'ceremonie', name: 'Cérémonies' },
    { id: 'cocktail', name: 'Cocktails' },
    { id: 'soiree', name: 'Soirées' },
    { id: 'sax-dj', name: 'Sax + DJ' }
  ];

  // Filter portfolio items (exclude hero and profile photos from public view)
  const portfolioForPublic = portfolioItems.filter(item => 
    !['hero-accueil', 'about-profile'].includes(item.category)
  );

  const filteredPortfolio = portfolioForPublic.filter(item => 
    selectedCategory === 'all' || item.category === selectedCategory
  );

  return (
    <div className="min-h-screen">
      {/* Hero Section */}
      <section className="bg-gradient-to-br from-ivory via-pearl to-light-gold/20 py-20 md:py-32">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h1 className="text-4xl md:text-5xl font-bold text-charcoal mb-6 leading-tight">
            Portfolio
          </h1>
          <p className="text-xl text-warm-gray mb-8 max-w-3xl mx-auto leading-relaxed">
            Découvrez quelques-unes de nos plus belles réalisations et les témoignages 
            de couples qui nous ont fait confiance pour leur jour J.
          </p>
          
          {/* Category Filter */}
          <div className="flex flex-wrap justify-center gap-3 mt-12">
            {categories.map((category) => (
              <button
                key={category.id}
                onClick={() => setSelectedCategory(category.id)}
                data-testid={`filter-${category.id}`}
                className={`px-6 py-3 rounded-full transition-all duration-300 text-sm font-medium border-2 ${
                  selectedCategory === category.id
                    ? 'bg-blue-900 border-blue-900 text-white shadow-lg'
                    : 'bg-white border-blue-900 text-blue-900 hover:bg-blue-900 hover:text-white'
                }`}
              >
                {category.name}
              </button>
            ))}
          </div>
        </div>
      </section>

      {/* Photo Gallery */}
      <section className="py-20 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-12">
            <h2 className="text-3xl md:text-4xl font-bold text-charcoal mb-4">
              Galerie Photos
            </h2>
            <p className="text-lg text-warm-gray">
              Aperçu de nos prestations lors d'événements exceptionnels
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {filteredPortfolio.length > 0 ? (
              filteredPortfolio.map((item) => (
                <Card key={item.id} className="group overflow-hidden border-0 shadow-lg hover:shadow-xl transition-all duration-300">
                  <div className="relative aspect-[4/3]">
                    {item.image_url ? (
                      <img 
                        src={item.image_url} 
                        alt={item.title}
                        className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-300"
                      />
                    ) : (
                      <div className="bg-gradient-to-br from-ivory to-pearl/50 flex items-center justify-center h-full">
                        <div className="text-center">
                          <div className="bg-gradient-to-r from-blue-900 to-blue-700 p-4 rounded-full mb-4 mx-auto">
                            <Camera className="h-8 w-8 text-white" />
                          </div>
                          <p className="text-warm-gray font-medium">Photo à venir</p>
                        </div>
                      </div>
                    )}
                    
                    {/* Overlay on hover */}
                    <div className="absolute inset-0 bg-charcoal/80 opacity-0 group-hover:opacity-100 transition-all duration-300 flex items-center justify-center">
                      <div className="text-center text-white p-6">
                        <h3 className="text-lg font-bold mb-2">{item.title}</h3>
                        <p className="text-sm text-white/80">{item.description}</p>
                      </div>
                    </div>
                  </div>
                  
                  <CardContent className="p-4">
                    <h3 className="font-semibold text-charcoal group-hover:text-blue-900 transition-colors">
                      {item.title}
                    </h3>
                    <p className="text-sm text-warm-gray">{item.description}</p>
                  </CardContent>
                </Card>
              ))
            ) : (
              <div className="col-span-full text-center py-12">
                <Camera className="h-12 w-12 text-warm-gray/50 mx-auto mb-4" />
                <p className="text-warm-gray">Aucune réalisation dans cette catégorie.</p>
              </div>
            )}
          </div>
        </div>
      </section>

      {/* Testimonials Section */}
      <section className="py-20 bg-gradient-to-br from-ivory/30 to-pearl/30">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <h2 className="text-3xl md:text-4xl font-bold text-charcoal mb-4">
              Témoignages
            </h2>
            <p className="text-xl text-warm-gray max-w-2xl mx-auto">
              Ce que disent les couples qui nous ont fait confiance
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
            {testimonials.map((testimonial) => (
              <Card key={testimonial.id} className="border-0 bg-white/80 backdrop-blur-sm hover:shadow-lg transition-all duration-300 group">
                <CardContent className="p-6">
                  <div className="flex items-center justify-between mb-4">
                    <div className="flex items-center">
                      {[...Array(testimonial.rating)].map((_, i) => (
                        <Star key={i} className="h-4 w-4 text-gold fill-current" />
                      ))}
                    </div>
                    <Quote className="h-6 w-6 text-gold/30" />
                  </div>
                  
                  <p className="text-charcoal mb-6 italic leading-relaxed">
                    "{testimonial.comment}"
                  </p>
                  
                  <div className="border-t border-warm-gray/20 pt-4">
                    <div className="flex items-center justify-between">
                      <div>
                        <p className="font-semibold text-charcoal">{testimonial.name}</p>
                        <div className="flex items-center space-x-2 text-sm text-warm-gray">
                          <Calendar className="h-3 w-3" />
                          <span>{testimonial.date}</span>
                        </div>
                      </div>
                      <div className="text-right">
                        <div className="flex items-center space-x-1 text-xs text-gold">
                          <MapPin className="h-3 w-3" />
                          <span>Événement privé</span>
                        </div>
                      </div>
                    </div>
                    <p className="text-xs text-warm-gray mt-2 italic">{testimonial.event}</p>
                  </div>
                </CardContent>
              </Card>
            ))}
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-20 bg-gradient-to-r from-blue-900 to-blue-700">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h2 className="text-3xl md:text-4xl font-bold text-white mb-6">
            Votre histoire mérite une bande sonore unique
          </h2>
          <p className="text-xl text-white/90 mb-8 leading-relaxed">
            Rejoignez nos clients satisfaits et créons ensemble des souvenirs musicaux inoubliables.
          </p>
          <Button 
            size="lg"
            asChild
            className="bg-white text-blue-900 hover:bg-ivory hover:text-charcoal text-lg px-8 py-4 hover:shadow-lg transition-all duration-300"
            data-testid="portfolio-cta-contact"
          >
            <Link to="/contact">
              Discutons de votre projet
            </Link>
          </Button>
        </div>
      </section>
    </div>
  );
};
